/**
 * "Extract a document" — run the real track-record extractor on ONE loan
 * document (`POST /experience/extract_document/{dealId}/{documentId}`) and
 * seed candidates from what it finds.
 *
 * Lists the loan's documents with the Experience and Track Record group first,
 * then proof-hint matches, then the rest; a Borrower / Contractor toggle sets
 * `context`. The run is long (LLM extraction): the button disables and shows
 * progress. The extractor is the shape gate — a document that is not a
 * consolidated schedule comes back `rejected` with a reason, shown verbatim.
 */
import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import CloseRounded from "@mui/icons-material/CloseRounded";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import SearchOutlined from "@mui/icons-material/SearchOutlined";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { filePreviewSetData } from "../../../redux/reducers/filePreviewReducer";
import { slate, teal } from "../../../theme/colors";
import { isDocHidden } from "../../../_src_shared/utils/functions/isDocHidden";
import { docName, EXPERIENCE_DOC_GROUP, proofRank, sortProofDocs } from "./experienceConstants";

const CONTEXTS = [
  { value: "borrower", label: "Borrower", hint: "Borrower Experience schedules" },
  { value: "contractor", label: "Contractor", hint: "Contractor Experience schedules" },
];

const ExperienceExtractDialog = ({ open, onClose, loan, busy, onExtract }) => {
  const dispatch = useDispatch();
  const [selected, setSelected] = useState(null);
  const [context, setContext] = useState("borrower");
  const [query, setQuery] = useState("");
  const [result, setResult] = useState(null);

  // Hidden documents are never offered.
  const docs = useMemo(() => sortProofDocs((loan?.loanDocuments || []).filter((d) => !isDocHidden(d))), [loan?.loanDocuments]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? docs.filter((d) => `${docName(d)} ${d.docType || ""} ${d.docGroup || ""}`.toLowerCase().includes(q)) : docs;
  }, [docs, query]);

  const run = async () => {
    if (!selected) return;
    setResult(null);
    try {
      const out = await onExtract(selected._id, context);
      setResult(out || {});
    } catch (err) {
      setResult({ extraction: { ok: false, error: err?.message || "failed" } });
    }
  };

  const ext = result?.extraction;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "12px" } }}>
      <Flx ac jb g={2} sx={{ px: 3, py: 2, borderBottom: `1px solid ${slate[200]}` }}>
        <Box>
          <Txt sx={{ display: "block", fontSize: 16, fontWeight: 700, color: slate[900] }}>Extract a document</Txt>
          <Txt sx={{ fontSize: 12, color: slate[500] }}>
            Run the track-record extractor on one document and seed candidate items from it. Nothing is verified.
          </Txt>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={!!busy}>
          <CloseRounded sx={{ fontSize: 18 }} />
        </IconButton>
      </Flx>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Flx ac g={0.75} sx={{ mb: 2 }}>
          <Txt sx={{ fontSize: 12.5, color: slate[700] }}>Schedule context</Txt>
          {CONTEXTS.map((c) => (
            <Chip
              key={c.value}
              label={c.label}
              title={c.hint}
              onClick={() => setContext(c.value)}
              sx={{
                height: 26,
                fontSize: 12,
                fontWeight: 600,
                bgcolor: context === c.value ? teal[600] : "#fff",
                color: context === c.value ? "#fff" : slate[700],
                border: `1px solid ${context === c.value ? teal[600] : slate[300]}`,
              }}
            />
          ))}
        </Flx>

        <TextField
          size="small"
          fullWidth
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search loan documents…"
          InputProps={{ startAdornment: <SearchOutlined sx={{ fontSize: 18, color: slate[400], mr: 0.75 }} /> }}
        />
        <Box sx={{ mt: 1, maxHeight: 320, overflowY: "auto", border: `1px solid ${slate[200]}`, borderRadius: "8px" }}>
          {filtered.map((d) => {
            const on = selected?._id === d._id;
            const rank = proofRank(d);
            return (
              <Flx
                key={d._id}
                ac
                g={1}
                onClick={() => setSelected(d)}
                sx={{ px: 1.25, py: 0.75, cursor: "pointer", borderBottom: `1px solid ${slate[100]}`, bgcolor: on ? teal[50] : "#fff", "&:hover": { bgcolor: teal[50] } }}
              >
                <DescriptionOutlined sx={{ fontSize: 15, color: rank === 0 ? teal[600] : slate[400] }} />
                <Txt
                  onClick={(ev) => {
                    ev.stopPropagation();
                    dispatch(filePreviewSetData(d));
                  }}
                  sx={{ fontSize: 12.5, flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textDecoration: "underline", textDecorationColor: slate[200] }}
                >
                  {docName(d)}
                </Txt>
                <Txt sx={{ fontSize: 11, color: d.docGroup === EXPERIENCE_DOC_GROUP ? teal[700] : slate[500], flexShrink: 0, fontWeight: rank === 0 ? 700 : 400 }}>
                  {d.docType || d.docGroup || ""}
                </Txt>
              </Flx>
            );
          })}
          {!filtered.length ? <Txt sx={{ display: "block", fontSize: 12, color: slate[500], p: 1.5 }}>No documents match.</Txt> : null}
        </Box>

        {ext ? (
          <Box sx={{ mt: 2 }}>
            {ext.ok ? (
              <Alert severity="success" sx={{ fontSize: 12.5 }}>
                Extracted <b>{ext.documentName || docName(selected)}</b>
                {ext.ranAsDocType && ext.ranAsDocType !== ext.originalDocType ? ` (ran as ${ext.ranAsDocType})` : ""} —
                added {result.added ?? 0} · skipped {result.skippedExisting ?? 0} already present · {result.candidatesFound ?? 0} candidates found.
              </Alert>
            ) : (
              <Alert severity={ext.rejected ? "warning" : "error"} sx={{ fontSize: 12.5 }}>
                {ext.rejected ? "Not a consolidated track-record schedule" : "Extraction failed"}
                {ext.error ? ` — ${ext.error}` : ""}
                {ext.reclassification ? ` (${typeof ext.reclassification === "string" ? ext.reclassification : JSON.stringify(ext.reclassification)})` : ""}
              </Alert>
            )}
          </Box>
        ) : null}
      </DialogContent>

      <Flx ac jb g={1} sx={{ px: 3, py: 1.5, borderTop: `1px solid ${slate[200]}` }}>
        <Txt sx={{ fontSize: 11.5, color: slate[500] }}>
          {busy === "extract" ? "Running the extractor — this takes a while…" : "The loan's Activity Log records the run."}
        </Txt>
        <Flx ac g={1}>
          <Button size="small" variant="text" onClick={onClose} disabled={busy === "extract"}>
            {result ? "Done" : "Cancel"}
          </Button>
          <Button size="small" onClick={run} disabled={!selected || busy === "extract"} startIcon={busy === "extract" ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}>
            {busy === "extract" ? "Extracting…" : "Extract & seed"}
          </Button>
        </Flx>
      </Flx>
    </Dialog>
  );
};

ExperienceExtractDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  loan: PropTypes.object,
  busy: PropTypes.any,
  onExtract: PropTypes.func,
};

export default ExperienceExtractDialog;
